import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { ErrorMessageComponent } from '../../../backoffice/components/UI/ErrorMessage';
import { useController, Control } from 'react-hook-form';

interface AreaInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  control?: Control<any>;  // Usado com react-hook-form
  name: string;
  error?: any;
  hide?: boolean;
  valueDefault?: number;  // Corrigido para valueDefault
  solid: boolean;
}

export function AreaInput({
  label,
  hide,
  control,
  name,
  error,
  solid,
  valueDefault,
  ...props
}: AreaInputProps) {
  const { field } = useController({ name, control });
  const [displayValue, setDisplayValue] = useState('');
  const isEditingRef = useRef(false);

  // Funcao para formatar o valor como area (m2)
  const formatArea = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const numberValue = typeof value === 'string' ? Number(value) : value;
    if (Number.isNaN(numberValue)) {
      return '';
    }
    const hasDecimals = numberValue % 1 !== 0;
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

  const normalizeDecimalInput = (value: string) => {
    let cleaned = value.replace(/[^\d,.]/g, '');
    if (cleaned.includes(',')) {
      cleaned = cleaned.replace(/\./g, '');
      const [intPart, decPart] = cleaned.split(',');
      return `${intPart}${decPart !== undefined ? `.${decPart}` : ''}`;
    }
    if (cleaned.includes('.')) {
      const [intPart, decPart] = cleaned.split('.');
      return `${intPart}${decPart !== undefined ? `.${decPart}` : ''}`;
    }
    return cleaned;
  };

  // Manipulador de mudanca para garantir a formatacao correta
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    const rawValue = event.target.value;
    setDisplayValue(rawValue);
    const normalized = normalizeDecimalInput(rawValue);
    const numberValue = normalized === '' ? null : Number(normalized);
    field.onChange(numberValue);
  };

  const handleBlur = () => {
    isEditingRef.current = false;
    field.onBlur();
    setDisplayValue(formatArea(field.value ?? valueDefault));
  };

  useEffect(() => {
    if (!isEditingRef.current) {
      setDisplayValue(formatArea(field.value ?? valueDefault));
    }
  }, [field.value, valueDefault]);

  return (
    <div className={hide ? 'hide' : 'form-group'} hidden={hide}>
      {label && <label className={hide ? 'd-none' : 'col-form-label'}>{label}</label>}
      <div className="input-group mb-3">
        <input
          {...props}
          {...field}
          className={`form-control${solid ? ' form-control-solid' : ''}`}
          placeholder="Ex: 100 m2"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <span className="input-group-text bg-white border-1">m2</span>
      </div>
      {error && <ErrorMessageComponent message={error} />}
    </div>
  );
}
